import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, useState } from 'react';
import * as CollectionAPI from '../services/CollectionAPI';
import { isHitRarity, matchesRarityBucket } from '../utils/constants';
import { getCardVariants } from '../utils/cardVariants';
import { getOwnedSetIds, hasAllSetsFetched, ensurePricesLoaded, calculatePortfolioValue, getCardRarity } from '../utils/portfolioValue';
import { MAX_DEX } from '../data/nationalDex';
import { cardIdBelongsToSet } from '../utils/setMerges';

// Action types
const ActionTypes = {
  SET_COLLECTION: 'SET_COLLECTION',
  UPDATE_CARD_QUANTITY: 'UPDATE_CARD_QUANTITY',
  SET_PACK_STATS: 'SET_PACK_STATS',
  UPDATE_SET_PACK_COUNT: 'UPDATE_SET_PACK_COUNT',
  SET_SETTINGS: 'SET_SETTINGS',
  SET_CURRENT_SET: 'SET_CURRENT_SET',
  SET_CARDS: 'SET_CARDS',
  SET_ALL_SETS: 'SET_ALL_SETS',
  SET_FILTERS: 'SET_FILTERS',
  SET_SEARCH: 'SET_SEARCH',
  SET_LOADING: 'SET_LOADING',
  SET_SERVER_STATUS: 'SET_SERVER_STATUS',
  CLEAR_COLLECTION: 'CLEAR_COLLECTION',
  UPDATE_ENERGY_STAT: 'UPDATE_ENERGY_STAT',
  SET_POKEDEX_BINDER: 'SET_POKEDEX_BINDER',
  SET_POKEDEX_SLOT: 'SET_POKEDEX_SLOT',
  REMOVE_POKEDEX_SLOT: 'REMOVE_POKEDEX_SLOT'
};

// Helper function to recalculate series totals from set stats
const recalculateSeriesStats = (setStats, allSets) => {
  const seriesStats = {};
  
  // Group sets by series
  const seriesGroups = {};
  allSets.forEach(set => {
    const series = set.series || 'Other';
    if (!seriesGroups[series]) {
      seriesGroups[series] = [];
    }
    seriesGroups[series].push(set.id);
  });
  
  // Calculate totals for each series
  Object.keys(seriesGroups).forEach(series => {
    const setIds = seriesGroups[series];
    let totalPacks = 0;
    let totalHits = 0;
    
    setIds.forEach(setId => {
      const setData = setStats[setId];
      if (setData) {
        totalPacks += setData.packs || 0;
        totalHits += setData.hits || 0;
      }
    });
    
    seriesStats[series] = { packs: totalPacks, hits: totalHits };
  });
  
  return seriesStats;
};

// Helper function to calculate total packs across all sets
const calculateTotalPacks = (setStats) => {
  return Object.values(setStats).reduce((total, setData) => {
    return total + (setData.packs || 0);
  }, 0);
};

// Initial state
const initialState = {
  // Collection data
  collection: {},
  packStats: { totalPacks: 0, seriesStats: {}, setStats: {} },
  // Pokédex binder — sparse map of dexNumber → snapshot of the chosen card
  pokedexBinder: {},
  settings: {
    completionView: 'base',
    theme: 'light',
    autoSave: true
  },
  
  // Pokemon data
  allSets: [],
  currentSet: null,
  cards: [],
  
  // UI state
  filters: {
    search: '',
    rarity: 'all',
    type: 'all',
    ownership: 'all',
    artist: 'all'  // ADDED ARTIST FILTER
  },
  globalSearch: {
    term: '',
    results: [],
    loading: false
  },
  
  // App state
  loading: false,
  serverOnline: false,
  currentView: 'series',
  selectedSeries: null
};

// Reducer function
const collectionReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_COLLECTION:
      return { ...state, collection: action.payload };
      
    case ActionTypes.UPDATE_CARD_QUANTITY:
      const { cardId, variant, quantity } = action.payload;
      const newCollection = {
        ...state.collection,
        [cardId]: {
          ...state.collection[cardId],
          [variant]: Math.max(0, quantity)
        }
      };
      return { ...state, collection: newCollection };
      
    case ActionTypes.SET_PACK_STATS: {
      const incoming = action.payload || {};
      return {
        ...state,
        packStats: {
          totalPacks: incoming.totalPacks || 0,
          seriesStats: incoming.seriesStats || {},
          setStats: incoming.setStats || {}
        }
      };
    }
      
    case ActionTypes.UPDATE_SET_PACK_COUNT: {
      const { setId, packCount } = action.payload;

      const validPackCount = Math.max(0, Math.min(999, packCount));
      const existingSet = state.packStats.setStats[setId] || {};

      const newSetStats = {
        ...state.packStats.setStats,
        [setId]: {
          ...existingSet,
          packs: validPackCount,
          // A manual pack decrement can drop packs below hits — hits can
          // never exceed packs.
          hits: Math.min(existingSet.hits || 0, validPackCount),
          lastOpened: new Date().toISOString().split('T')[0]
        }
      };

      // allSets loads lazily; recalculating against an empty list would
      // return {} and the auto-save would persist that, wiping seriesStats
      // on the server. Keep the existing seriesStats until sets are
      // available — SET_ALL_SETS heals them once they load.
      const newSeriesStats = state.allSets.length > 0
        ? recalculateSeriesStats(newSetStats, state.allSets)
        : state.packStats.seriesStats;

      return {
        ...state,
        packStats: {
          ...state.packStats,
          setStats: newSetStats,
          seriesStats: newSeriesStats,
          totalPacks: calculateTotalPacks(newSetStats)
        }
      };
    }
      
    case ActionTypes.SET_SETTINGS:
      const newSettings = { ...state.settings, ...action.payload };
      return { ...state, settings: newSettings };
      
    case ActionTypes.SET_CURRENT_SET:
      return { ...state, currentSet: action.payload };
      
    case ActionTypes.SET_CARDS:
      // Auto-add variants to cards when they're loaded
      const cardsWithVariants = action.payload.map(card => ({
        ...card,
        variants: getCardVariants(card)
      }));
      return { ...state, cards: cardsWithVariants };
      
    case ActionTypes.SET_ALL_SETS: {
      const newAllSets = action.payload;

      if (!newAllSets || newAllSets.length === 0) {
        return { ...state, allSets: newAllSets || [] };
      }

      // Heal seriesStats now that the set→series mapping is known: covers
      // stats saved while allSets was empty, and repairs any account whose
      // seriesStats were wiped by the old empty-recalc bug. Keep the same
      // packStats reference when nothing changed so the auto-save effect
      // doesn't fire a pointless PUT.
      const healedSeriesStats = recalculateSeriesStats(state.packStats.setStats, newAllSets);
      const changed = JSON.stringify(healedSeriesStats) !== JSON.stringify(state.packStats.seriesStats);

      return {
        ...state,
        allSets: newAllSets,
        packStats: changed
          ? { ...state.packStats, seriesStats: healedSeriesStats }
          : state.packStats
      };
    }
      
    case ActionTypes.SET_FILTERS:
      return { 
        ...state, 
        filters: { ...state.filters, ...action.payload } 
      };
      
    case ActionTypes.SET_SEARCH:
      return {
        ...state,
        globalSearch: { ...state.globalSearch, ...action.payload }
      };
      
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case ActionTypes.SET_SERVER_STATUS:
      return { ...state, serverOnline: action.payload };
      
    case ActionTypes.UPDATE_ENERGY_STAT: {
      const { setId: esSetId, energyType, delta, isHit, hitDelta } = action.payload;
      const existingSetStats = state.packStats.setStats[esSetId] || {};
      const existingEnergyStats = existingSetStats.energyStats || {};
      const existing = existingEnergyStats[energyType] || { packs: 0, hits: 0 };

      const newPacks = Math.max(0, existing.packs + delta);
      // hitDelta lets a caller remove a mis-clicked hit directly (BUG-004);
      // when omitted, fall back to the original add-a-hit-on-★ behavior.
      const effectiveHitDelta = hitDelta ?? (isHit && delta > 0 ? 1 : 0);
      // Cap hits: can't exceed packs, can't go below 0
      let newHits = existing.hits + effectiveHitDelta;
      newHits = Math.min(newHits, newPacks);
      newHits = Math.max(0, newHits);

      // Every energy log IS a pack log — each opened pack contains exactly
      // one energy card, so logging an energy (with or without a ★ hit)
      // also counts a pack (and hit) at the set level. This is what feeds
      // every set/series/global hit-rate display.
      const packsDelta = newPacks - existing.packs;
      const hitsDelta = newHits - existing.hits;
      const newSetPacks = Math.max(0, Math.min(999, (existingSetStats.packs || 0) + packsDelta));
      const newSetHits = Math.max(0, Math.min(newSetPacks, (existingSetStats.hits || 0) + hitsDelta));

      const newSetStats = {
        ...state.packStats.setStats,
        [esSetId]: {
          ...existingSetStats,
          packs: newSetPacks,
          hits: newSetHits,
          ...(delta > 0 ? { lastOpened: new Date().toISOString().split('T')[0] } : {}),
          energyStats: {
            ...existingEnergyStats,
            [energyType]: { packs: newPacks, hits: newHits }
          }
        }
      };

      // Same empty-allSets guard as UPDATE_SET_PACK_COUNT.
      const newSeriesStats = state.allSets.length > 0
        ? recalculateSeriesStats(newSetStats, state.allSets)
        : state.packStats.seriesStats;

      return {
        ...state,
        packStats: {
          ...state.packStats,
          setStats: newSetStats,
          seriesStats: newSeriesStats,
          totalPacks: calculateTotalPacks(newSetStats)
        }
      };
    }

    case ActionTypes.SET_POKEDEX_BINDER:
      return { ...state, pokedexBinder: action.payload || {} };

    case ActionTypes.SET_POKEDEX_SLOT: {
      const { dexNumber, slot } = action.payload;
      return {
        ...state,
        pokedexBinder: { ...state.pokedexBinder, [dexNumber]: slot }
      };
    }

    case ActionTypes.REMOVE_POKEDEX_SLOT: {
      const newBinder = { ...state.pokedexBinder };
      delete newBinder[action.payload];
      return { ...state, pokedexBinder: newBinder };
    }

    case ActionTypes.CLEAR_COLLECTION:
      return {
        ...state,
        collection: {},
        packStats: { totalPacks: 0, seriesStats: {}, setStats: {} }
      };

    default:
      return state;
  }
};

// Create context
const CollectionContext = createContext();

// Context provider component
export const CollectionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(collectionReducer, initialState);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  const initialLoadDone = useRef(false);

  // Lazily fetch prices for any sets the user owns cards from — the price
  // cache lives outside React state (module-level in portfolioValue.js), so
  // once a fetch resolves we bump a counter purely to force a re-render.
  const [, forcePriceRefresh] = useReducer((c) => c + 1, 0);
  useEffect(() => {
    const setIds = getOwnedSetIds(state.collection);
    if (setIds.length === 0 || hasAllSetsFetched(setIds)) return;

    let cancelled = false;
    setPortfolioLoading(true);
    ensurePricesLoaded(setIds).then(() => {
      if (cancelled) return;
      setPortfolioLoading(false);
      forcePriceRefresh();
    });

    return () => {
      cancelled = true;
    };
  }, [state.collection]);

  // Initial user-data load. The UI is gated on this (see the render below):
  // no edits can happen before the server data is in state, which closes
  // the race where an early edit was both blocked from saving AND then
  // overwritten by the arriving server response.
  const [initialLoadState, setInitialLoadState] = useState('loading'); // 'loading' | 'ready' | 'error'
  const skipNextSaveRef = useRef({ packStats: false, settings: false });

  const loadUserData = useCallback(async () => {
    setInitialLoadState('loading');
    try {
      const [collection, packStats, settings, pokedexBinder] = await Promise.all([
        CollectionAPI.getCollection(),
        CollectionAPI.getPackStats(),
        CollectionAPI.getSettings(),
        CollectionAPI.getPokedexBinder()
      ]);
      // The dispatches below change packStats/settings state, which would
      // trigger the auto-save effects and echo the just-loaded data straight
      // back to the server — flag them to skip one save each.
      skipNextSaveRef.current.packStats = true;
      skipNextSaveRef.current.settings = true;
      dispatch({ type: ActionTypes.SET_COLLECTION, payload: collection });
      dispatch({ type: ActionTypes.SET_PACK_STATS, payload: packStats });
      dispatch({ type: ActionTypes.SET_SETTINGS, payload: settings });
      dispatch({ type: ActionTypes.SET_POKEDEX_BINDER, payload: pokedexBinder });
      initialLoadDone.current = true;
      setInitialLoadState('ready');
    } catch (err) {
      // Deliberately do NOT set initialLoadDone here: saves stay blocked so
      // a transient load failure can't lead to default/empty state being
      // PUT over the user's real server data.
      console.error('Failed to load user data from server:', err);
      setInitialLoadState('error');
    }
  }, []);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // A failed auto-save used to only console.error — edits looked saved all
  // session and were gone next launch. Track failures and show a banner.
  const [saveError, setSaveError] = useState(false);

  // Auto-save pack stats to server when they change
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (skipNextSaveRef.current.packStats) {
      skipNextSaveRef.current.packStats = false;
      return;
    }
    CollectionAPI.savePackStats(state.packStats)
      .then(() => setSaveError(false))
      .catch(err => {
        console.error('Failed to save pack stats:', err);
        setSaveError(true);
      });
  }, [state.packStats]);

  // Auto-save settings to server when they change
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (skipNextSaveRef.current.settings) {
      skipNextSaveRef.current.settings = false;
      return;
    }
    CollectionAPI.saveSettings(state.settings)
      .then(() => setSaveError(false))
      .catch(err => {
        console.error('Failed to save settings:', err);
        setSaveError(true);
      });
  }, [state.settings]);

  // Banner retry: re-PUT the CURRENT state (never a stale snapshot, which
  // could land after a newer save and roll it back).
  const retrySave = () => {
    Promise.all([
      CollectionAPI.savePackStats(state.packStats),
      CollectionAPI.saveSettings(state.settings)
    ])
      .then(() => setSaveError(false))
      .catch(err => console.error('Retry save failed:', err));
  };
  
  // Collection operations
  const updateCardQuantity = (cardId, variant, quantity) => {
    dispatch({
      type: ActionTypes.UPDATE_CARD_QUANTITY,
      payload: { cardId, variant, quantity }
    });
  };
  
  const updateSetPackCount = (setId, packCount, setInfo = null) => {
    dispatch({
      type: ActionTypes.UPDATE_SET_PACK_COUNT,
      payload: { setId, packCount, setInfo }
    });
  };
  
  const getTotalQuantity = (cardId) => {
    const cardData = state.collection[cardId];
    if (!cardData) return 0;
    return Object.values(cardData).reduce((sum, qty) => sum + qty, 0);
  };
  
  const getVariantQuantity = (cardId, variant) => {
    return state.collection[cardId]?.[variant] || 0;
  };
  
  const getSetPackCount = (setId) => {
    return state.packStats.setStats[setId]?.packs || 0;
  };

  const updateEnergyStat = (setId, energyType, delta, isHit = false, hitDelta) => {
    dispatch({
      type: ActionTypes.UPDATE_ENERGY_STAT,
      payload: { setId, energyType, delta, isHit, hitDelta }
    });
  };

  const getEnergyStats = (setId) => {
    return state.packStats.setStats[setId]?.energyStats || {};
  };

  const getEnergyTypeStats = (setId, energyType) => {
    return state.packStats.setStats[setId]?.energyStats?.[energyType] || { packs: 0, hits: 0 };
  };
  
  // FIXED: Completion calculations
  const calculateCompletion = () => {
    if (!state.currentSet || state.cards.length === 0) {
      return { collected: 0, total: 0 };
    }
    
    if (state.settings.completionView === 'base') {
      // FIXED: Base set = 1 copy of each unique card (including secrets)
      const totalUniqueCards = state.cards.length;
      const collectedUniqueCards = state.cards.filter(card => 
        getTotalQuantity(card.id) > 0
      ).length;
      
      return { collected: collectedUniqueCards, total: totalUniqueCards };
    } else {
      // Master set = 1 copy of each variant of each card
      let totalVariants = 0;
      let collectedVariants = 0;
      
      state.cards.forEach(card => {
        card.variants.forEach(variant => {
          totalVariants++;
          if (getVariantQuantity(card.id, variant) > 0) {
            collectedVariants++;
          }
        });
      });
      
      return { collected: collectedVariants, total: totalVariants };
    }
  };

  // NEW: Calculate completion for a specific set (base set only)
  const calculateSetCompletion = (setId, setTotal) => {
    if (!setId || !setTotal) {
      return { collected: 0, total: 0, percentage: 0 };
    }
    
    // Find all cards in collection that belong to this set (merged child
    // sets count toward their parent — BUG-010)
    const setCardIds = Object.keys(state.collection).filter(cardId =>
      cardIdBelongsToSet(cardId, setId)
    );
    
    // Count unique cards owned (base set = 1 copy of each unique card)
    const collectedUniqueCards = setCardIds.filter(cardId => 
      getTotalQuantity(cardId) > 0
    ).length;
    
    const percentage = setTotal > 0 ? Math.round((collectedUniqueCards / setTotal) * 100) : 0;
    
    return {
      collected: collectedUniqueCards,
      total: setTotal,
      percentage
    };
  };
  
  // Collection statistics
  const getCollectionStats = () => {
    let totalCards = 0;
    let uniqueCards = 0;

    Object.keys(state.collection).forEach(cardId => {
      const cardQuantities = state.collection[cardId];
      const cardTotal = Object.values(cardQuantities).reduce((sum, qty) => sum + qty, 0);

      if (cardTotal > 0) {
        totalCards += cardTotal;
        uniqueCards += 1;
      }
    });

    return {
      totalCards,
      uniqueCards,
      totalValue: calculatePortfolioValue(state.collection),
      totalPacks: state.packStats.totalPacks
    };
  };

  // Estimated market value of the whole collection. Prices are fetched
  // lazily per owned set (see the effect above) — this reads whatever is
  // in the cache right now, so it starts at 0 and grows as sets resolve.
  const getPortfolioValue = () => calculatePortfolioValue(state.collection);
  
  // Hits are derived from the collection: owned cards whose rarity is in
  // HIT_RARITIES (EX/full art/secret). Rarity comes from the module-level
  // cache in portfolioValue.js, filled by the same lazy per-owned-set fetch
  // that prices the portfolio. Pass a setId to scope to one set.
  const countOwnedHits = (setId = null) => {
    let hits = 0;
    Object.entries(state.collection).forEach(([cardId, variants]) => {
      if (setId && !cardIdBelongsToSet(cardId, setId)) return;
      if (!isHitRarity(getCardRarity(cardId))) return;
      hits += Object.values(variants).reduce((sum, qty) => sum + qty, 0);
    });
    return hits;
  };

  // Hit rate for one set: owned hit-rarity cards from that set ÷ packs
  // opened for it. (The per-energy ★ tracker is a separate feature and
  // doesn't feed this.)
  const getSetTrackedHitRate = (setId) => {
    const setData = state.packStats.setStats[setId];
    if (!setData || !setData.packs) return 0;
    return Math.round((countOwnedHits(setId) / setData.packs) * 100);
  };

  // Global hit rate: every owned hit-rarity card ÷ every pack opened.
  // null (rendered as "—") until packs exist and rarity data has loaded,
  // so a slow fetch doesn't flash a false 0%.
  const getGlobalHitRate = () => {
    let totalPacks = 0;
    Object.values(state.packStats.setStats).forEach(setData => {
      totalPacks += setData.packs || 0;
    });

    if (totalPacks === 0 || portfolioLoading) return null;
    return Math.round((countOwnedHits() / totalPacks) * 100);
  };
  
  const getFilteredCards = () => {
    return state.cards.filter(card => {
      const { search, rarity, type, ownership, artist } = state.filters;

      const matchesSearch = search === '' ||
        card.name.toLowerCase().includes(search.toLowerCase()) ||
        card.number.toString().includes(search);

      const matchesRarity = matchesRarityBucket(card.rarity, rarity);

      const matchesType = type === 'all' ||
        (card.types && card.types.includes(type));

      const matchesArtist = artist === 'all' || card.artist === artist;

      let matchesOwnership = true;
      if (ownership === 'owned' || ownership === 'not-owned') {
        if (state.settings.completionView === 'master') {
          const hasAllVariants = card.variants?.every(v => getVariantQuantity(card.id, v) > 0);
          matchesOwnership = ownership === 'owned' ? hasAllVariants : !hasAllVariants;
        } else {
          const hasAny = getTotalQuantity(card.id) > 0;
          matchesOwnership = ownership === 'owned' ? hasAny : !hasAny;
        }
      }

      return matchesSearch && matchesRarity && matchesType && matchesOwnership && matchesArtist;
    });
  };
  
  // Get unique filter options from current cards - UPDATED WITH ARTIST
  const getFilterOptions = () => {
    if (state.cards.length === 0) return { rarities: [], types: [], artists: [] };
    
    const rarities = [...new Set(state.cards.map(card => card.rarity))]
      .filter(Boolean)
      .sort();
      
    const types = [...new Set(state.cards.flatMap(card => card.types || []))]
      .filter(Boolean)
      .sort();

    // ADDED: Extract unique artists
    const artists = [...new Set(state.cards.map(card => card.artist))]
      .filter(Boolean)
      .sort();
    
    return { rarities, types, artists };
  };
  
  // Navigation helpers
  const setCurrentView = useCallback((view, data = {}) => {
    const newState = { currentView: view, ...data };
    
    // Clear filters when changing views
    if (view === 'series') {
      dispatch({ 
        type: ActionTypes.SET_FILTERS, 
        payload: { search: '', rarity: 'all', type: 'all', ownership: 'all', artist: 'all' }
      });
    }
    
    // Update state based on view
    Object.keys(newState).forEach(key => {
      if (key === 'currentView') return;
      
      switch (key) {
        case 'selectedSeries':
        case 'currentSet':
        case 'cards':
        case 'allSets':
          dispatch({ type: `SET_${key.toUpperCase()}`, payload: newState[key] });
          break;
      }
    });
  }, [dispatch]);
  
  // Memoized action helpers
  const setCollection = useCallback((collection) => 
    dispatch({ type: ActionTypes.SET_COLLECTION, payload: collection }), [dispatch]);
  
  const setPackStats = useCallback((stats) => 
    dispatch({ type: ActionTypes.SET_PACK_STATS, payload: stats }), [dispatch]);
  
  const setSettings = useCallback((settings) => 
    dispatch({ type: ActionTypes.SET_SETTINGS, payload: settings }), [dispatch]);
  
  const setCurrentSet = useCallback((set) => 
    dispatch({ type: ActionTypes.SET_CURRENT_SET, payload: set }), [dispatch]);
  
  const setCards = useCallback((cards) => 
    dispatch({ type: ActionTypes.SET_CARDS, payload: cards }), [dispatch]);
  
  const setAllSets = useCallback((sets) => 
    dispatch({ type: ActionTypes.SET_ALL_SETS, payload: sets }), [dispatch]);
  
  const setFilters = useCallback((filters) => 
    dispatch({ type: ActionTypes.SET_FILTERS, payload: filters }), [dispatch]);
  
  const setGlobalSearch = useCallback((search) => 
    dispatch({ type: ActionTypes.SET_SEARCH, payload: search }), [dispatch]);
  
  const setLoading = useCallback((loading) => 
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading }), [dispatch]);
  
  const setServerStatus = useCallback((status) => 
    dispatch({ type: ActionTypes.SET_SERVER_STATUS, payload: status }), [dispatch]);
  
  const clearCollection = useCallback(() =>
    dispatch({ type: ActionTypes.CLEAR_COLLECTION }), [dispatch]);

  // Pokédex binder — raw state dispatchers (persistence lives in
  // useCollection's addToPokedex/removeFromPokedex, mirroring how
  // updateCardQuantity/updateCard split state vs. server writes)
  const setPokedexSlot = useCallback((dexNumber, slot) =>
    dispatch({ type: ActionTypes.SET_POKEDEX_SLOT, payload: { dexNumber, slot } }), [dispatch]);

  const removePokedexSlotLocal = useCallback((dexNumber) =>
    dispatch({ type: ActionTypes.REMOVE_POKEDEX_SLOT, payload: dexNumber }), [dispatch]);

  const getPokedexSlot = (dexNumber) => state.pokedexBinder[dexNumber] || null;

  const getPokedexCompletion = () => ({
    filled: Object.keys(state.pokedexBinder).length,
    total: MAX_DEX
  });
  
  // Context value
  const value = {
    // State
    ...state,
    
    // Collection operations
    updateCardQuantity,
    getTotalQuantity,
    getVariantQuantity,
    
    // Pack stats operations
    updateSetPackCount,
    getSetPackCount,

    // Energy stat operations
    updateEnergyStat,
    getEnergyStats,
    getEnergyTypeStats,
    
    // Calculations
    calculateCompletion,
    calculateSetCompletion,  // NEW: Added set completion function
    getCollectionStats,
    getSetTrackedHitRate,
    getGlobalHitRate,
    getPortfolioValue,
    portfolioLoading,

    // Filters and search
    getFilteredCards,
    getFilterOptions,
    
    // Actions
    dispatch,
    setCurrentView,
    
    // Action helpers (now memoized)
    setCollection,
    setPackStats,
    setSettings,
    setCurrentSet,
    setCards,
    setAllSets,
    setFilters,
    setGlobalSearch,
    setLoading,
    setServerStatus,
    clearCollection,

    // Pokédex binder
    setPokedexSlot,
    removePokedexSlotLocal,
    getPokedexSlot,
    getPokedexCompletion
  };
  
  return (
    <CollectionContext.Provider value={value}>
      {initialLoadState === 'ready' && saveError && (
        <div className="fixed top-0 inset-x-0 z-50 bg-red-600 text-white text-sm px-4 py-2.5 flex items-center justify-center gap-3 shadow-md">
          <span>⚠ Your latest changes aren't saving to the server.</span>
          <button
            onClick={retrySave}
            className="underline font-semibold hover:text-red-100"
          >
            Retry
          </button>
        </div>
      )}
      {initialLoadState === 'ready' ? (
        children
      ) : (
        <div className="min-h-screen bg-cream-bg flex flex-col items-center justify-center px-6 text-center">
          {initialLoadState === 'loading' ? (
            <>
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy-dark border-t-transparent mb-4"></div>
              <span className="text-ink-secondary text-sm">Loading your collection...</span>
            </>
          ) : (
            <>
              <h2 className="font-heading font-bold text-lg text-ink-primary mb-2">
                Couldn't load your collection
              </h2>
              <p className="text-ink-secondary text-sm mb-5 max-w-xs">
                Something went wrong reaching the server. Your data is safe — nothing gets saved until it loads.
              </p>
              <button
                onClick={loadUserData}
                className="bg-navy-dark text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy-light transition-colors"
              >
                Retry
              </button>
            </>
          )}
        </div>
      )}
    </CollectionContext.Provider>
  );
};

// Custom hook to use the collection context
export const useCollectionContext = () => {
  const context = useContext(CollectionContext);
  if (!context) {
    throw new Error('useCollectionContext must be used within a CollectionProvider');
  }
  return context;
};

export default CollectionContext;